import React, { createContext, useContext, useReducer, useEffect, useRef, ReactNode } from 'react';
import { Location, LocationContextType, LocationSelectionState } from '../types/location.types';
import { locationService } from '../api/services/locationService';
import { useAuth } from './AuthContext';

// Action types
type LocationAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_LOCATIONS'; payload: Location[] }
  | { type: 'SET_SELECTED_LOCATIONS'; payload: string[] }
  | { type: 'ADD_SELECTED_LOCATION'; payload: string }
  | { type: 'REMOVE_SELECTED_LOCATION'; payload: string }
  | { type: 'UPDATE_LOCATION'; payload: Location }
  | { type: 'REMOVE_LOCATION'; payload: string };

// Initial state
const initialState: LocationSelectionState = {
  selectedLocationIds: [],
  availableLocations: [],
  loading: false,
  error: null,
};

// Reducer
const locationReducer = (state: LocationSelectionState, action: LocationAction): LocationSelectionState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    
    case 'SET_LOCATIONS':
      return { ...state, availableLocations: action.payload, loading: false, error: null };
    
    case 'SET_SELECTED_LOCATIONS':
      return { ...state, selectedLocationIds: action.payload };
    
    case 'ADD_SELECTED_LOCATION':
      if (state.selectedLocationIds.includes(action.payload)) {
        return state;
      }
      return {
        ...state,
        selectedLocationIds: [...state.selectedLocationIds, action.payload],
      };
    
    case 'REMOVE_SELECTED_LOCATION':
      return {
        ...state,
        selectedLocationIds: state.selectedLocationIds.filter(id => id !== action.payload),
      };
    
    case 'UPDATE_LOCATION':
      return {
        ...state,
        availableLocations: state.availableLocations.map(location =>
          location.id === action.payload.id ? action.payload : location
        ),
      };
    
    case 'REMOVE_LOCATION':
      return {
        ...state,
        availableLocations: state.availableLocations.filter(location => location.id !== action.payload),
        selectedLocationIds: state.selectedLocationIds.filter(id => id !== action.payload),
      };
    
    default:
      return state;
  }
};

// Create context
const LocationContext = createContext<LocationContextType | undefined>(undefined);

// Provider props
interface LocationProviderProps {
  children: ReactNode;
}

// Provider component
export const LocationProvider: React.FC<LocationProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(locationReducer, initialState);
  const { business, token, isAuthenticated } = useAuth();

  // Helper to save selection to localStorage
  const saveToLocalStorage = (locationIds: string[]) => {
    if (!business?.id) return;
    
    try {
      const key = `selectedLocationIds_${business.id}`;
      localStorage.setItem(key, JSON.stringify(locationIds));
    } catch (error) {
      console.warn('Failed to save location selection to localStorage:', error);
    }
  };

  // Helper to load selection from localStorage (business-specific)
  const loadFromLocalStorage = (): string[] => {
    if (!business?.id) return [];
    
    try {
      const key = `selectedLocationIds_${business.id}`;
      const saved = localStorage.getItem(key);
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.warn('Failed to load location selection from localStorage:', error);
      return [];
    }
  };

  // Initialize locations and selected locations
  const hasInitializedRef = useRef<string | null>(null);
  const isInitializingRef = useRef(false);
  
  // Reset initialization flag when business changes
  useEffect(() => {
    if (hasInitializedRef.current !== business?.id) {
      hasInitializedRef.current = null;
    }
  }, [business?.id]);
  
  useEffect(() => {
    const initializeLocations = async () => {
      // Prevent concurrent initialization
      if (isInitializingRef.current) {
        console.log('⏸️ Already initializing locations, skipping...');
        return;
      }

      if (!business?.id) {
        console.log('⏳ Waiting for business data...');
        return;
      }

      if (!isAuthenticated || !token) {
        console.log('⏳ Waiting for user authentication...', {
          isAuthenticated,
          hasToken: !!token,
          tokenPreview: token ? token.substring(0, 20) + '...' : 'none'
        });
        return;
      }

      // Only initialize once per business
      if (hasInitializedRef.current === business.id) {
        console.log('✅ Locations already initialized for this business');
        return;
      }

      console.log('✅ Business and authentication ready, initializing locations...', {
        businessId: business.id,
        isAuthenticated,
        hasToken: !!token
      });

      isInitializingRef.current = true;

      // Clear any old localStorage entries from previous businesses
      // This ensures complete multi-tenant isolation
      try {
        const keys = Object.keys(localStorage);
        keys.forEach(key => {
          if (key.startsWith('selectedLocationIds_') && !key.includes(business.id)) {
            localStorage.removeItem(key);
          }
        });
      } catch (error) {
        console.warn('Failed to clean up old localStorage entries:', error);
      }

      dispatch({ type: 'SET_LOADING', payload: true });

      try {
        // Fetch locations from API only - no mock data fallback
        console.log(`🔍 Fetching locations for business: ${business.id}`);
        const locations = await locationService.getBusinessLocations(business.id);
        console.log(`✅ Received ${locations.length} locations from API:`, locations);

        dispatch({ type: 'SET_LOCATIONS', payload: locations });
        
        // Debug log to verify multi-tenancy
        console.log(`🏢 Loading locations for business: ${business.id}`, {
          businessId: business.id,
          locationCount: locations.length,
          locationIds: locations.map(l => ({ id: l.id, name: l.location_name, business_id: l.business_id }))
        });

        // Load user's selected locations from localStorage
        let selectedIds: string[] = loadFromLocalStorage();

        // If no selection, default to all locations (empty array means all locations)
        if (selectedIds.length === 0) {
          selectedIds = []; // Empty array = All locations selected
        }

        // CRITICAL: Filter out invalid location IDs and ensure all locations belong to current business
        const validLocationIds = locations.map(loc => loc.id);
        const filteredSelectedIds = selectedIds.filter(id => {
          // Must be a valid location ID
          if (!validLocationIds.includes(id)) return false;
          
          // Must belong to current business (extra safety check)
          const location = locations.find(loc => loc.id === id);
          return location && location.business_id === business.id;
        });

        dispatch({ type: 'SET_SELECTED_LOCATIONS', payload: filteredSelectedIds });
        saveToLocalStorage(filteredSelectedIds);
        hasInitializedRef.current = business.id;

      } catch (error) {
        console.error('❌ Failed to initialize locations:', error);
        console.error('Error details:', {
          message: error instanceof Error ? error.message : 'Unknown error',
          businessId: business.id,
          error
        });
        
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        if (errorMessage.includes('429')) {
          dispatch({ type: 'SET_ERROR', payload: 'Too many requests. Please wait a moment and refresh the page.' });
        } else {
          dispatch({ type: 'SET_ERROR', payload: `Failed to load locations: ${errorMessage}` });
        }
      } finally {
        isInitializingRef.current = false;
      }
    };

    initializeLocations();
  }, [business?.id, isAuthenticated, token]);

  // Computed values
  const selectedLocations = state.availableLocations.filter(location =>
    state.selectedLocationIds.includes(location.id)
  );

  const getPrimaryLocation = (): Location | null => {
    return state.availableLocations.find(location => location.is_primary) || null;
  };

  // Actions
  const selectLocation = async (locationId: string) => {
    dispatch({ type: 'ADD_SELECTED_LOCATION', payload: locationId });
    
    const newSelection = [...state.selectedLocationIds, locationId];
    saveToLocalStorage(newSelection);
    
    // Save to backend
    try {
      await locationService.saveUserSelectedLocations(newSelection);
    } catch (error) {
      console.warn('Failed to save location selection to backend:', error);
    }
  };

  const deselectLocation = async (locationId: string) => {
    dispatch({ type: 'REMOVE_SELECTED_LOCATION', payload: locationId });
    
    const newSelection = state.selectedLocationIds.filter(id => id !== locationId);
    saveToLocalStorage(newSelection);
    
    // Save to backend
    try {
      await locationService.saveUserSelectedLocations(newSelection);
    } catch (error) {
      console.warn('Failed to save location selection to backend:', error);
    }
  };

  const setSelectedLocations = async (locationIds: string[]) => {
    dispatch({ type: 'SET_SELECTED_LOCATIONS', payload: locationIds });
    saveToLocalStorage(locationIds);
    
    // Save to backend
    try {
      await locationService.saveUserSelectedLocations(locationIds);
    } catch (error) {
      console.warn('Failed to save location selection to backend:', error);
    }
  };

  const refreshLocations = async () => {
    if (!business?.id) return;

    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      const locations = await locationService.getBusinessLocations(business.id);
      dispatch({ type: 'SET_LOCATIONS', payload: locations });
    } catch (error) {
      console.error('Failed to refresh locations:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to refresh locations' });
    }
  };

  const isLocationSelected = (locationId: string): boolean => {
    return state.selectedLocationIds.includes(locationId);
  };

  const isAllLocationsSelected = (): boolean => {
    const activeLocations = state.availableLocations.filter(loc => loc.is_active);
    return state.selectedLocationIds.length === 0 || 
           (state.selectedLocationIds.length === activeLocations.length && 
            activeLocations.every(loc => state.selectedLocationIds.includes(loc.id)));
  };

  const getSelectedLocationDisplayName = (): string => {
    if (isAllLocationsSelected()) {
      return 'All locations';
    } else if (state.selectedLocationIds.length === 1) {
      const location = state.availableLocations.find(loc => loc.id === state.selectedLocationIds[0]);
      return location?.location_name || 'Unknown location';
    } else if (state.selectedLocationIds.length > 1) {
      return `${state.selectedLocationIds.length} locations`;
    }
    return 'All locations';
  };

  // Context value
  const contextValue: LocationContextType = {
    locations: state.availableLocations,
    selectedLocations,
    selectedLocationIds: state.selectedLocationIds,
    loading: state.loading,
    error: state.error,
    selectLocation,
    deselectLocation,
    setSelectedLocations,
    refreshLocations,
    isLocationSelected,
    getPrimaryLocation,
    isAllLocationsSelected,
    getSelectedLocationDisplayName,
  };

  return (
    <LocationContext.Provider value={contextValue}>
      {children}
    </LocationContext.Provider>
  );
};

// Hook to use the context
export const useLocation = (): LocationContextType => {
  const context = useContext(LocationContext);
  if (context === undefined) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
};

export default LocationContext;
